import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'

interface Props {
  text: string
  children?: React.ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
}

/**
 * AppTooltip — muestra un tooltip al hacer hover sobre el ícono (?) o cualquier children.
 *
 * Uso básico:
 *   <AppTooltip text="Texto explicativo" />
 *
 * Con children personalizados:
 *   <AppTooltip text="Texto explicativo"><span>?</span></AppTooltip>
 */
const AppTooltip = ({ text, children, position = 'top' }: Props) => {
  const [visible, setVisible] = useState(false)
  const [positioned, setPositioned] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const positionTooltip = () => {
    if (!triggerRef.current || !tooltipRef.current) return
    const triggerRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()

    let top = 0
    let left = 0
    const GAP = 6

    // El tooltip usa `position: fixed`, por lo que las coordenadas son
    // relativas al viewport. getBoundingClientRect() ya devuelve coordenadas
    // relativas al viewport, así que NO se debe sumar window.scrollX/scrollY
    // (eso duplicaría el desplazamiento del scroll y descolocaría el tooltip).
    switch (position) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - GAP
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
        break
      case 'bottom':
        top = triggerRect.bottom + GAP
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
        break
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
        left = triggerRect.left - tooltipRect.width - GAP
        break
      case 'right':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
        left = triggerRect.right + GAP
        break
    }

    setCoords({ top, left })
    setPositioned(true)
  }

  useEffect(() => {
    if (!visible) {
      setPositioned(false)
      return
    }

    positionTooltip()

    // Reposicionar mientras el tooltip está visible para que siga al trigger
    // al hacer scroll o redimensionar la ventana.
    const handleReposition = () => positionTooltip()
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [visible])

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        tabIndex={0}
        aria-label={text}
        aria-describedby={visible ? 'app-tooltip' : undefined}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold cursor-default select-none leading-none focus:outline-none focus:ring-2 focus:ring-gray-400"
      >
        {children ?? '?'}
      </span>

      {visible &&
        createPortal(
          <div
            ref={tooltipRef}
            id="app-tooltip"
            role="tooltip"
            style={{
              top: coords.top,
              left: coords.left,
              visibility: positioned ? 'visible' : 'hidden',
            }}
            className="fixed z-50 max-w-[260px] rounded-md bg-[#1E293B] text-white text-xs leading-relaxed px-3 py-2 shadow-lg pointer-events-none"
          >
            {text}
            {/* Flecha decorativa */}
            <span
              className={`absolute w-2 h-2 bg-[#1E293B] rotate-45 ${
                position === 'top'
                  ? 'bottom-[-4px] left-1/2 -translate-x-1/2'
                  : position === 'bottom'
                  ? 'top-[-4px] left-1/2 -translate-x-1/2'
                  : position === 'left'
                  ? 'right-[-4px] top-1/2 -translate-y-1/2'
                  : 'left-[-4px] top-1/2 -translate-y-1/2'
              }`}
            />
          </div>,
          document.body
        )}
    </>
  )
}

export default AppTooltip
